import { useRef, useState } from 'react';
import {
  describeExplainError,
  explainCorrectionText,
  speakText,
  MAX_EXPLAIN_LEVEL,
  type ExplainLangResolved,
  type ExplainCorrectionTextResult,
} from '../../../lib/englishApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import { ExplanationContent } from './ExplanationContent.js';
import { resolveLookup } from '../../../components/word-lookup/index.js';
import {
  IconClose,
  IconPen,
  IconPlay,
  IconWaveSmall,
  IconArrowRight,
  IconChevron,
  IconLanguageSmall,
} from '../icons.js';
import type { ConvError } from '../errorModel.js';
import { GRAM_SEVERITY_STYLE } from '../severityStyle.js';
import { CorrectionThumbs } from './CorrectionThumbs.js';
import { DiffText, useCorrectionMarks } from '../../../components/correction-diff/index.js';

/** Mini botón de play para escuchar audio (idle/loading/playing). */
function PlayDot({ playing, loading, accent, onClick }: { playing: boolean; loading: boolean; accent: string; onClick: () => void }) {
  return (
    <button
      className="err-play"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{ color: accent }}
      aria-label="Escuchar"
    >
      {playing || loading ? (
        <div className="bars playing" style={{ height: 9, color: accent }}>
          <span />
          <span />
          <span />
        </div>
      ) : (
        <IconPlay size={9} />
      )}
    </button>
  );
}

/** Mini botón de traducción (mismo tamaño que PlayDot). */
function TranslateDot({ active, loading, accent, onClick }: { active: boolean; loading: boolean; accent: string; onClick: () => void }) {
  return (
    <button
      className={`err-play ${active ? 'is-active' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{ color: accent }}
      aria-label={active ? 'Ocultar traducción' : 'Traducir'}
      title={active ? 'Ocultar traducción' : 'Traducir'}
    >
      {loading ? (
        <span className="dots-anim" style={{ fontSize: 9 }} />
      ) : (
        <IconLanguageSmall size={11} />
      )}
    </button>
  );
}

/** Tag de cada card de explicación según su nivel de profundización. */
const EXPAND_TAGS = ['EXPLICACIÓN', 'MÁS DETALLE', 'AÚN MÁS A FONDO'] as const;

/**
 * Una card de explicación expandida (un nivel). Cada card tiene su propio play
 * (TTS del campo inglés) — por eso es un componente: el hook `useAudioBlob` no
 * puede vivir dentro de un `.map`. El toggle de idioma es compartido (lo maneja
 * el padre vía `onToggleLang`/`shownLang`), así todas las cards cambian juntas.
 */
function ExpansionCard({
  expansion,
  index,
  shownLang,
  onToggleLang,
  sessionId,
}: {
  expansion: ExplainCorrectionTextResult;
  index: number;
  shownLang: ExplainLangResolved;
  onToggleLang: () => void;
  sessionId?: string;
}) {
  const explainTts = useAudioBlob();
  const body = shownLang === 'target' ? expansion.en : expansion.es;
  return (
    <div className="err-expand-box">
      <div className="err-expand-head">
        <span className="err-expand-tag">{EXPAND_TAGS[index] ?? 'MÁS'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            className="err-expand-lang"
            onClick={onToggleLang}
            title={shownLang === 'target' ? 'Ver en español' : 'Ver en inglés'}
          >
            {shownLang === 'target' ? 'ES' : 'EN'}
          </button>
          <PlayDot
            playing={explainTts.state === 'playing'}
            loading={explainTts.state === 'loading'}
            accent="var(--lime)"
            onClick={() => void explainTts.toggle(() => speakText(expansion.en, sessionId ? { sessionId } : {}))}
          />
        </div>
      </div>
      <div className="err-expand-body">
        <ExplanationContent text={body} />
      </div>
    </div>
  );
}

interface ErrorDetailProps {
  err: ConvError;
  onClose: () => void;
  /**
   * Abre los drills de pronunciación. Sólo disponible en la sesión viva; en el
   * historial se omite y el CTA "Practicar drills" no se renderiza.
   */
  onPractice?: (err: ConvError) => void;
  sessionId?: string;
  /**
   * Navegación prev/next entre los errores del turno. Cuando hay más de uno,
   * la card muestra "‹ N/total ›" para recorrerlos sin tener que cazar el
   * subrayado exacto. `index` es 1-based.
   */
  nav?: {
    index: number;
    total: number;
    onPrev: () => void;
    onNext: () => void;
  };
  /**
   * Idioma persistido de la "Expandir explicación" (`null` = sin elección →
   * el backend resuelve por nivel). Viene de las prefs de conversación.
   */
  explainLang?: ExplainLangResolved | null;
  /** Persiste la elección de idioma de la explicación (toggle del user). */
  onSetExplainLang?: (lang: ExplainLangResolved) => void;
}

export function ErrorDetail({ err, onClose, onPractice, sessionId, nav, explainLang, onSetExplainLang }: ErrorDetailProps) {
  const isGram = err.kind === 'grammar';
  const gramStyle = GRAM_SEVERITY_STYLE[err.severity];
  const accent = isGram ? gramStyle.accent : '#fbbf24';
  const tint = isGram ? gramStyle.tint : 'rgba(251,191,36,0.08)';
  const border = isGram ? gramStyle.border : 'rgba(251,191,36,0.22)';

  // Swipe horizontal para pasar de error (cuando hay varios). Mismo gesto que
  // las flechas, más natural en mobile. Sólo dispara si el desplazamiento es
  // claramente horizontal (no confundir con el scroll vertical de la card).
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_THRESHOLD = 45;
  const onTouchStart = (e: React.TouchEvent): void => {
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent): void => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!nav || !start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) nav.onNext(); // swipe ←  → siguiente
    else nav.onPrev(); //         swipe →  → anterior
  };

  // Qué palabras cambian entre lo que dijo y la forma buena: se marcan en las
  // dos filas para no tener que compararlas a ojo. Vacío en las fonéticas (no
  // hay dos frases) y en las reescrituras completas — ver `correctionMarks`.
  const marks = useCorrectionMarks(err.wrong, err.right);

  // Audio: "Mejor así" → TTS de la forma correcta (err.right);
  // cada card de explicación tiene su propio play (ver ExpansionCard);
  // fonética → TTS de la palabra.
  const rightTts = useAudioBlob();
  const wordTts = useAudioBlob();

  // Traducción on-demand de "Mejor así" (al español, como las burbujas del coach).
  const [rightTranslation, setRightTranslation] = useState<string | null>(null);
  const [rightTransLoading, setRightTransLoading] = useState(false);
  const toggleRightTranslation = (): void => {
    if (rightTranslation !== null) {
      setRightTranslation(null);
      return;
    }
    setRightTransLoading(true);
    void resolveLookup(err.right, 'corrección gramatical', sessionId)
      .then((r) => setRightTranslation(r.translation))
      .catch(() => setRightTranslation('No pudimos traducir esta frase.'))
      .finally(() => setRightTransLoading(false));
  };

  // Explicación expandida (texto bilingüe, on-demand). Acumulativa: el user
  // puede tocar "Expandir más" hasta MAX_EXPLAIN_LEVEL veces; cada nivel agrega
  // una card más profunda con ejemplos concretos. Guardamos todas las cards.
  const [expansions, setExpansions] = useState<ExplainCorrectionTextResult[]>([]);
  const [expandLoading, setExpandLoading] = useState(false);
  // Motivo del último fallo (`null` = sin fallo). Guardamos el MOTIVO y no un
  // booleano a propósito: "No se pudo generar" tapaba por igual un backend
  // caído, una sesión vencida y un round-trip perdido, así que un reporte del
  // user no alcanzaba para saber qué había pasado.
  const [expandError, setExpandError] = useState<string | null>(null);
  // En la sesión viva el idioma elegido se persiste en las prefs vía
  // `onSetExplainLang`; en el historial (sin prefs) lo manejamos local para que
  // el toggle EN/ES igual funcione. Precedencia: pref persistida → local → la
  // que resolvió el backend (de la primera card). Es compartido entre todas las
  // cards (un solo toggle las cambia a todas).
  const [localLang, setLocalLang] = useState<ExplainLangResolved | null>(null);
  const shownLang: ExplainLangResolved = explainLang ?? localLang ?? expansions[0]?.lang ?? 'native';
  const canExpandMore = expansions.length < MAX_EXPLAIN_LEVEL;

  /** Pide el siguiente nivel de explicación y lo agrega como card nueva. */
  const expandMore = (): void => {
    if (expandLoading || !canExpandMore) return;
    setExpandLoading(true);
    setExpandError(null);
    const level = expansions.length + 1;
    const previous = expansions.map((e) => e.en);
    void explainCorrectionText({
      said: err.wrong,
      fix: err.right,
      explanation: err.note || null,
      lang: explainLang ?? 'auto',
      level,
      ...(previous.length > 0 ? { previous } : {}),
      ...(sessionId ? { sessionId } : {}),
    })
      .then((r) => setExpansions((prev) => [...prev, r]))
      .catch((e: unknown) => {
        // `explainCorrectionText` ya reintentó sola los fallos transitorios:
        // si llegamos acá, los 3 intentos fallaron o el fallo era determinista.
        console.warn('[expand-explain] la explicación expandida falló', e);
        setExpandError(describeExplainError(e));
      })
      .finally(() => setExpandLoading(false));
  };
  const toggleExpandLang = (): void => {
    const next: ExplainLangResolved = shownLang === 'target' ? 'native' : 'target';
    if (onSetExplainLang) onSetExplainLang(next);
    else setLocalLang(next);
  };

  return (
    <div
      className="err-detail slide-up"
      style={{ background: tint, borderColor: border }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="err-detail-icon" style={{ background: accent, color: '#0c0d15' }}>
            {isGram ? <IconPen size={12} /> : <IconWaveSmall size={12} />}
          </div>
          <span className="tag" style={{ color: accent, letterSpacing: 1.4 }}>
            {isGram ? gramStyle.tag : 'PRONUNCIACIÓN'}
          </span>
          {isGram && err.spoken && (
            <span
              className="tag"
              title="El coach dijo esta corrección en voz alta"
              style={{
                color: 'var(--text-faint)',
                letterSpacing: 0.8,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="11"
                height="11"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M11 5 L 6 9 H 2 V 15 H 6 L 11 19 Z" />
                <path d="M15.5 8.5 a 5 5 0 0 1 0 7" />
              </svg>
              DICHA EN VOZ ALTA
            </span>
          )}
        </div>
        {nav && nav.total > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nav.onPrev();
              }}
              aria-label="Error anterior"
              className="err-nav-btn"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.08)',
                color: accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}>
                <IconChevron size={16} />
              </span>
            </button>
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-muted)',
                minWidth: 30,
                textAlign: 'center',
              }}
            >
              {nav.index}/{nav.total}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nav.onNext();
              }}
              aria-label="Error siguiente"
              className="err-nav-btn"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.08)',
                color: accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'inline-flex', transform: 'rotate(-90deg)' }}>
                <IconChevron size={16} />
              </span>
            </button>
          </div>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Cerrar"
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.06)',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <IconClose size={11} />
        </button>
      </div>

      {isGram ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div className="err-label">Dijiste</div>
            <div className="err-row err-row-wrong">
              <span className={gramStyle.strike ? 'err-strike' : ''}>
                <DiffText text={err.wrong} marks={marks.said} color={gramStyle.accent} />
              </span>
            </div>
          </div>
          <div>
            <div className="err-label">{gramStyle.betterLabel}</div>
            <div className="err-row err-row-right" style={{ borderColor: 'rgba(212,242,62,0.3)' }}>
              <span style={{ color: 'var(--lime)', fontWeight: 500 }}>
                <DiffText text={err.right} marks={marks.fix} color="var(--lime)" />
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <TranslateDot
                  active={rightTranslation !== null}
                  loading={rightTransLoading}
                  accent="var(--lime)"
                  onClick={toggleRightTranslation}
                />
                <PlayDot
                  playing={rightTts.state === 'playing'}
                  loading={rightTts.state === 'loading'}
                  accent="var(--lime)"
                  onClick={() => void rightTts.toggle(() => speakText(err.right, sessionId ? { sessionId } : {}))}
                />
              </div>
            </div>
            {rightTranslation !== null && <div className="err-translation">{rightTranslation}</div>}
          </div>
          {err.note && (
            <div className="err-note">
              <span className="script" style={{ color: accent, fontSize: 17, marginRight: 6 }}>
                Por qué:
              </span>
              {err.note}
            </div>
          )}

          {/* Expandir explicación: cards bilingües acumulativas (idioma según
              nivel/elección), cada una más profunda con ejemplos. Hasta
              MAX_EXPLAIN_LEVEL. Toggle de idioma compartido, play por card. */}
          {expansions.map((exp, i) => (
            <ExpansionCard
              key={i}
              expansion={exp}
              index={i}
              shownLang={shownLang}
              onToggleLang={toggleExpandLang}
              {...(sessionId ? { sessionId } : {})}
            />
          ))}
          {expandLoading && (
            <div className="err-note" style={{ opacity: 0.7 }}>
              Generando explicación<span className="dots-anim" />
            </div>
          )}
          {expandError !== null && (
            <button
              type="button"
              className="err-expand-cta"
              onClick={() => {
                setExpandError(null);
                expandMore();
              }}
            >
              <span>{expandError.charAt(0).toUpperCase() + expandError.slice(1)}. Reintentar</span>
            </button>
          )}
          {!expandLoading && expandError === null && canExpandMore && (
            <button type="button" className="err-expand-cta" onClick={expandMore}>
              <IconPen size={11} />
              <span>{expansions.length === 0 ? 'Expandir explicación' : 'Expandir más'}</span>
            </button>
          )}

          {/* Thumbs 👍/👎 (plan 26 §7.4) — sólo gramática y sólo cuando hay
              clave natural completa (sessionId + turnIndex informado por el
              origen). El voto se remonta al cambiar de error (key) para que el
              estado no se arrastre entre correcciones. */}
          {sessionId && err.turnIndex !== undefined && (
            <CorrectionThumbs
              key={err.id}
              sessionId={sessionId}
              turnIndex={err.turnIndex}
              said={err.wrong}
              fix={err.right}
              initialFeedback={err.feedback}
            />
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontFamily: 'Caveat, cursive', fontWeight: 600, fontSize: 28, color: 'var(--text)', lineHeight: 1, padding: '4px 0 2px' }}>
            &quot;{err.word}&quot;
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="err-row err-row-wrong" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <span style={{ fontSize: 10, color: accent, letterSpacing: 1, fontWeight: 600 }}>TÚ</span>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  color: '#fca5a5',
                  fontSize: 13,
                  textDecoration: 'line-through',
                  textDecorationColor: 'rgba(252,165,165,0.55)',
                }}
              >
                {err.yours}
              </span>
            </div>
            <div className="err-row err-row-right" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6, borderColor: 'rgba(212,242,62,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: 10, color: 'var(--lime)', letterSpacing: 1, fontWeight: 600 }}>CORRECTO</span>
                <PlayDot
                  playing={wordTts.state === 'playing'}
                  loading={wordTts.state === 'loading'}
                  accent="var(--lime)"
                  onClick={() => void wordTts.toggle(() => speakText(err.word, sessionId ? { sessionId } : {}))}
                />
              </div>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--lime)', fontSize: 13 }}>{err.ipa}</span>
            </div>
          </div>
          {err.note && (
            <div className="err-note">
              <span className="script" style={{ color: accent, fontSize: 17, marginRight: 6 }}>
                Tip:
              </span>
              {err.note}
            </div>
          )}
          {onPractice && (
            <button
              className="drill-cta"
              onClick={(e) => {
                e.stopPropagation();
                onPractice(err);
              }}
            >
              <IconWaveSmall size={12} />
              <span>Practicar drills</span>
              <IconArrowRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
